import { useState } from "react";
import { useSelector } from "react-redux";

import { Add } from "@mui/icons-material";
import { Badge, IconButton, Popover } from "@mui/material";

import { getRandomIntInclusive } from "core/digit_utils";
import { getFilterTypes } from "slices/books/booksSelectors";

import styles from "./SearchSettings.module.css";
import { SearchSettingsButton } from "./SearchSettingsButton";
import { SearchSettingsCheckbox } from "./SearchSettingsCheckbox";
import { SearchSettingsCondition } from "./SearchSettingsCondition";
import { SearchSettingsRadioButtons } from "./SearchSettingsRadioButtons";

export const SearchSettingsKeyEnum = {
	FIELD: "field",
	CASE_SENSITIVE: "case_sensitive",
	EXCLUDE_PUNCTUATION: "exclude_punctuation",
	LOGICAL: "logical",
	FILTERS: "filters",
};

export const createNewCondition = (id, operationId) => ({ id, operation: operationId, value: "" });

export const getInitAdvancedSearchParams = ({ columnNames, filterTypes }) => {
	const initFilterType = filterTypes.find(({ name }) => name === "Содержит");

	return columnNames.map((clmName) => ({
		[SearchSettingsKeyEnum.FIELD]: clmName,
		[SearchSettingsKeyEnum.CASE_SENSITIVE]: false,
		[SearchSettingsKeyEnum.EXCLUDE_PUNCTUATION]: false,
		[SearchSettingsKeyEnum.LOGICAL]: "AND",
		[SearchSettingsKeyEnum.FILTERS]: [createNewCondition(getRandomIntInclusive(1, 1000), initFilterType?.id)],
	}));
};

export const SearchSettings = ({ openPopoverIcon, columnName, value, setValue, ...props }) => {
	const filterTypes = useSelector(getFilterTypes);
	const initFilterType = filterTypes.find(({ name }) => name === "Содержит");

	const [anchorEl, setAnchorEl] = useState(null);

	const settings = value.find((clmSet) => clmSet[SearchSettingsKeyEnum.FIELD] === columnName);
	const notEmptyFilterCount = settings?.[SearchSettingsKeyEnum.FILTERS].filter(({ value }) => Boolean(value)).length;

	const updateSetting = ({ name, newValue }) =>
		setValue((prev) =>
			prev.map((clmSet) => {
				if (clmSet[SearchSettingsKeyEnum.FIELD] === columnName) {
					return {
						...clmSet,
						[name]: newValue,
					};
				}
				return clmSet;
			})
		);

	return (
		<>
			<Badge
				color="primary"
				overlap="circular"
				badgeContent={notEmptyFilterCount}
				sx={{
					"& .MuiBadge-standard": {
						padding: "0px 3px",
						minWidth: "10px",
						height: "10px",
						top: "35%",
						right: "25%",
						fontWeight: 400,
						fontSize: "6px",
					},
				}}
			>
				<IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ padding: "4px" }}>
					{openPopoverIcon}
				</IconButton>
			</Badge>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={() => setAnchorEl(null)}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "right",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "right",
				}}
				{...props}
			>
				<div className={styles.container}>
					<div className={styles.checkboxContainer}>
						<SearchSettingsCheckbox
							label="Учитывать регистр"
							value={settings?.[SearchSettingsKeyEnum.CASE_SENSITIVE]}
							setValue={(newValue) =>
								updateSetting({
									name: SearchSettingsKeyEnum.CASE_SENSITIVE,
									newValue,
								})
							}
						/>
						<SearchSettingsCheckbox
							label="Без учёта знаков препинания"
							value={settings?.[SearchSettingsKeyEnum.EXCLUDE_PUNCTUATION]}
							setValue={(newValue) =>
								updateSetting({
									name: SearchSettingsKeyEnum.EXCLUDE_PUNCTUATION,
									newValue,
								})
							}
						/>
						<SearchSettingsRadioButtons
							value={settings?.[SearchSettingsKeyEnum.LOGICAL]}
							setValue={(newValue) =>
								updateSetting({
									name: SearchSettingsKeyEnum.LOGICAL,
									newValue,
								})
							}
						/>
					</div>
					{settings?.[SearchSettingsKeyEnum.FILTERS].map((condition) => (
						<SearchSettingsCondition
							key={condition.id}
							columnName={columnName}
							condition={condition}
							setValue={setValue}
							conditionOptions={filterTypes}
							showDeleteButton={settings?.[SearchSettingsKeyEnum.FILTERS].length > 1}
						/>
					))}
					<SearchSettingsButton
						startIcon={<Add />}
						onClick={() =>
							setValue((prev) =>
								prev.map((clmSet) => {
									if (clmSet[SearchSettingsKeyEnum.FIELD] === columnName) {
										clmSet[SearchSettingsKeyEnum.FILTERS].push(
											createNewCondition(getRandomIntInclusive(1, 1000), initFilterType?.id)
										);
									}
									return clmSet;
								})
							)
						}
					>
						Добавить условие
					</SearchSettingsButton>
				</div>
			</Popover>
		</>
	);
};
